<template>
  <Teleport to="body">
    <Transition name="float-fade" @after-leave="onClosed?.()">
      <div v-show="show" class="floating-match-status">
        <!-- 渐变背景容器 -->
        <div class="floating-content">
          <!-- 左侧当前用户头像 -->
          <div class="avatar-container">
            <div class="avatar avatar-left">
              <img v-if="userInfo.avatar" :src="userInfo.avatar" alt="current user" />
            </div>
            <div class="avatar avatar-right">
              <img v-if="currentAvatarUrl" :src="currentAvatarUrl" alt="matched user" />
            </div>
            <!-- 跳动的爱心 -->
            <div class="match-heart-container">
              <img class="match-heart" src="@/assets/images/icons/match-heart.svg" alt="match heart" />
            </div>
          </div>

          <!-- 右侧取消按钮 -->
          <div class="cancel-btn" @click="handleCancel">
            <img src="@/assets/images/icons/phone-hang-up.svg" alt="cancel" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, watch } from 'vue'
import { useUserStore } from '@/stores/user'
import { useVideoCallStore, VIDEO_CALL_STATUS } from '@/stores/videoCall'
import { useGlobalStore } from '@/stores/global'

defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  // 当前显示的头像 URL（由 useRandomMatch 计算并传入）
  currentAvatarUrl: {
    type: String,
    default: '',
  },
  // 过渡动画是否在初始渲染时生效（内部使用，由 usePopupState 传入）
  transitionAppear: {
    type: Boolean,
    default: false,
  },
  // 关闭且过渡动画结束后回调（用于卸载 DOM，由 functionCall 传入）
  onClosed: {
    type: Function,
    default: null,
  },
})

const emit = defineEmits(['update:show', 'cancel'])

const userStore = useUserStore()
const videoCallStore = useVideoCallStore()
const globalStore = useGlobalStore()

const userInfo = computed(() => {
  return userStore.userInfo || {}
})

// 监听匹配状态：仅在 MATCHING → IDLE 时视为取消匹配，通知父组件
// MATCHING → CALLING/CONNECTED 表示匹配成功，不应触发取消逻辑
watch(
  () => videoCallStore.callStatus,
  (newStatus, oldStatus) => {
    if (oldStatus === VIDEO_CALL_STATUS.MATCHING && newStatus === VIDEO_CALL_STATUS.IDLE) {
      emit('cancel')
    }
  }
)

// 监听页面可见性：如果页面不可见，则取消匹配
watch(
  () => globalStore.isDocumentVisible,
  (isDocumentVisible) => {
    if (!isDocumentVisible) {
      emit('cancel')
    }
  }
)
const updateShow = (show) => {
  emit('update:show', show)
}

const handleCancel = () => {
  updateShow(false)
  emit('cancel')
}

</script>

<style lang="less" scoped>
.floating-match-status {
  position: fixed;
  top: 100px;
  left: 0;
  width: 144px;
  height: 56px;
  z-index: 100;
  cursor: pointer;

  .floating-content {
    position: relative;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    background: linear-gradient(359deg, rgba(199, 100, 255, 0.9) 0.87%, rgba(127, 74, 255, 0.9) 104.88%);
    border: 1.5px solid #E4D5FF;
    border-left: none;
    border-radius: 0 16px 16px 0;
    display: flex;
    align-items: center;
    gap: 12px;
    padding-left: 16px;
    padding-right: 8px;

    // 头像容器
    .avatar-container {
      flex: 1;
      display: flex;
      justify-content: space-between;
      position: relative;

      .avatar {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        overflow: hidden;
        border: 1px solid rgba(255, 255, 255, 0.8);
        position: relative;
        z-index: 2;

        >img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      // 飞行的爱心容器
      .match-heart-container {
        width: 50px;
        height: 24px;
        .absolute-center();
        pointer-events: none;
        z-index: 10;
      }

      .match-heart {
        width: 32px;
        height: 32px;
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        animation: heart-beat 1.2s ease-in-out infinite;
      }
    }

    // 取消按钮
    .cancel-btn {
      width: 28px;
      height: 28px;

      >img {
        width: 100%;
        height: 100%;
      }
    }
  }
}

// 爱心跳动动画（心跳效果）
@keyframes heart-beat {

  0%,
  100% {
    transform: translate(-50%, -50%) scale(0.8);
    // opacity: 0.6;
  }

  10% {
    transform: translate(-50%, -50%) scale(1);
    // opacity: 1;
  }

  20% {
    transform: translate(-50%, -50%) scale(0.9);
    // opacity: 0.9;
  }

  30% {
    transform: translate(-50%, -50%) scale(1.1);
    // opacity: 1;
  }

  40%,
  90% {
    transform: translate(-50%, -50%) scale(0.8);
    // opacity: 0.6;
  }
}

// 过渡动画
.float-fade-enter-active,
.float-fade-leave-active {
  transition: opacity 0.3s ease;
}

.float-fade-enter-from,
.float-fade-leave-to {
  opacity: 0;
}
</style>
